/**
 * V70-S — 인사관리 > 연장 근무 관리 (KR 전사원, 감사 대응)
 *
 * - 서버 callable `getHrKrOvertimeSummary` 1회 호출 (권한·KR 범위·재분류는 서버 SSoT)
 * - 컬럼: 이름 · 소속 부서 · 직위 · 직책 · 연장근무 · 야간근무 · 휴일근무 · 휴일 야간근무 · 대체휴무 지급 (시간:분)
 * - **승인 근무시간 기준** (실제 출퇴근 아님, 휴게 미차감).
 *   V70-S-2: 서버 원본 4개 항목 상호 배타 — 일반 근무일 06~22 연장 / 22~06 야간, 휴일 06~22 휴일 / 22~06 휴일 야간.
 *   V70-S-5: 표시(화면·엑셀)는 포함 기준 — 연장근무 = 연장 + 야간, 휴일근무 = 휴일 + 휴일 야간 (toInclusiveWorkDisplay).
 *            승인 근무시간 합계 = 표시 연장근무 + 표시 휴일근무 (집계 예외 제외).
 * - 직원 이름·시간 클릭 → 해당 월 근거(승인 신청별 재분류, 대체휴무 지급 이력) 모달
 * - 엑셀: 요약 · 근무 근거 · 대체휴무 지급 · 집계 기준 4개 시트 (화면에 로드된 데이터만, 추가 조회 없음)
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AlertCircle, AlertTriangle, ChevronLeft, ChevronRight, Download, Loader2, Search, X } from 'lucide-react';
import * as XLSX from 'xlsx';
import { httpsCallable } from 'firebase/functions';
import { getFunctions } from '../../firebase/utils';
import { useCurrentUser } from '../../shared/hooks/useCurrentUser';
import { useJobTitles } from '../../shared/hooks/useJobTitles';
import { usePositionLevels } from '../../shared/hooks/usePositionLevels';
import { buildJobTitleMaps, buildPositionLevelMaps, compareOrgMember } from '../../shared/utils/sortOrgMembers';
import {
  CompOffGrantDetailRow,
  EXCEPTION_LABELS,
  ORIGINAL_TYPE_LABELS,
  OvertimeEmployeeRow,
  OvertimeRequestRow,
  approvedRangeText,
  deriveGrantExportRow,
  deriveOvertimeExportRow,
  deriveRequestExportRow,
  formatDays,
  formatMinutesHm,
  segmentText,
} from './overtimeManagementFormat';
import { INCLUSIVE_WORK_NOTE, INCLUSIVE_WORK_TOTAL_NOTE, toInclusiveWorkDisplay } from '../../shared/utils/approvedWorkDisplay';

interface SummaryResponse {
  success: boolean;
  data: {
    month: string;
    period: { startDate: string; endDate: string };
    maxSelectableMonth: string;
    basis: string;
    limitations: string[];
    employees: OvertimeEmployeeRow[];
    requests: OvertimeRequestRow[];
    compOffGrants: CompOffGrantDetailRow[];
    summary: {
      employeeCount: number;
      approvedRequestCount: number;
      exceptionCount: number;
      exceptionMinutes: number;
      overtimeMinutes: number;
      nightMinutes: number;
      holidayMinutes: number;
      holidayNightMinutes: number;
      totalMinutes: number;
      compOffGrantMinutes: number;
      compOffCancelledCount: number;
    };
  };
}

/** KST 현재 월 (서버 응답 전 월 이동 상한) */
function currentKstMonth(): string {
  return new Date(Date.now() + 9 * 3600 * 1000).toISOString().slice(0, 7);
}
function addMonths(monthStr: string, delta: number): string {
  const [y, m] = monthStr.split('-').map(Number);
  const d = new Date(Date.UTC(y, m - 1 + delta, 1));
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`;
}
function formatMonthDisplay(monthStr: string): string {
  const [y, m] = monthStr.split('-').map(Number);
  return `${y}년 ${m}월`;
}
function formatLocalYmd(date: Date): string {
  return `${date.getFullYear()}${String(date.getMonth() + 1).padStart(2, '0')}${String(date.getDate()).padStart(2, '0')}`;
}

const th: React.CSSProperties = {
  padding: '10px 12px', textAlign: 'left', fontSize: '13px', fontWeight: 600, color: 'var(--color-gray-700)',
  background: 'var(--color-gray-50)', borderBottom: '1px solid var(--color-gray-200)', position: 'sticky', top: 0, zIndex: 1, whiteSpace: 'nowrap',
};
const thNum: React.CSSProperties = { ...th, textAlign: 'right' };
const td: React.CSSProperties = { padding: '10px 12px', fontSize: '13px', borderBottom: '1px solid var(--color-gray-100)', whiteSpace: 'nowrap' };
const tdNum: React.CSSProperties = { ...td, textAlign: 'right', fontVariantNumeric: 'tabular-nums' };
const linkBtn: React.CSSProperties = { background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: 'var(--color-primary, #2563eb)', font: 'inherit' };

export function HrOvertimeManagementPage() {
  const { user, isLoading: authLoading } = useCurrentUser();
  const [month, setMonth] = useState<string>(currentKstMonth());
  const [data, setData] = useState<SummaryResponse['data'] | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchText, setSearchText] = useState('');
  const [detailId, setDetailId] = useState<string | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const fetchIdRef = useRef(0);

  const { jobTitles } = useJobTitles({ status: 'active' });
  const { positionLevels } = usePositionLevels({ status: 'active' });
  const sortMaps = useMemo(() => ({ ...buildJobTitleMaps(jobTitles), ...buildPositionLevelMaps(positionLevels) }), [jobTitles, positionLevels]);

  const fetchData = useCallback(async (targetMonth: string, id: number) => {
    setLoading(true);
    setError(null);
    try {
      const fn = httpsCallable<{ month: string }, SummaryResponse>(getFunctions(), 'getHrKrOvertimeSummary');
      const res = await fn({ month: targetMonth });
      if (id !== fetchIdRef.current) return;
      setData(res.data.data);
    } catch (err: any) {
      if (id !== fetchIdRef.current) return;
      console.error('[HrOvertimeManagementPage] 조회 실패:', err);
      setData(null);
      setError(err?.message || '서버 오류가 발생했습니다.');
    } finally {
      if (id === fetchIdRef.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (authLoading || !user) return;
    const id = ++fetchIdRef.current;
    fetchData(month, id);
  }, [month, user, authLoading, fetchData]);

  const maxMonth = data?.maxSelectableMonth ?? currentKstMonth();
  const canGoNext = month < maxMonth;

  const sortedEmployees = useMemo(() => {
    const rows = data?.employees ?? [];
    return [...rows].sort((a, b) => compareOrgMember(
      { jobTitle: a.jobTitleName, positionName: a.positionName, name: a.name },
      { jobTitle: b.jobTitleName, positionName: b.positionName, name: b.name },
      sortMaps,
    ));
  }, [data, sortMaps]);
  const keyword = searchText.trim().toLowerCase();
  const filteredEmployees = useMemo(
    () => (keyword ? sortedEmployees.filter((e) => (e.name || '').toLowerCase().includes(keyword)) : sortedEmployees),
    [sortedEmployees, keyword],
  );
  const nameOf = useCallback((id: string) => data?.employees.find((e) => e.employeeId === id)?.name || id, [data]);

  const handleExcelDownload = () => {
    if (isExporting || !data) return;
    setIsExporting(true);
    try {
      if (filteredEmployees.length === 0) {
        alert('다운로드할 데이터가 없습니다.');
        return;
      }
      const ids = new Set(filteredEmployees.map((e) => e.employeeId));
      const wb = XLSX.utils.book_new();
      const addSheet = (rows: Record<string, string | number>[], name: string, header?: string[]) => {
        const ws = rows.length ? XLSX.utils.json_to_sheet(rows) : XLSX.utils.aoa_to_sheet([header ?? ['(데이터 없음)']]);
        XLSX.utils.book_append_sheet(wb, ws, name);
      };
      addSheet(filteredEmployees.map(deriveOvertimeExportRow), '연장근무관리');
      addSheet(data.requests.filter((r) => ids.has(r.employeeId)).map((r) => deriveRequestExportRow(r, nameOf)), '근무근거');
      addSheet(data.compOffGrants.filter((g) => ids.has(g.employeeId)).map((g) => deriveGrantExportRow(g, nameOf)), '대체휴무지급');
      const sd = toInclusiveWorkDisplay(data.summary);
      addSheet([
        { '항목': '조회 월', '내용': `${data.month} (${data.period.startDate} ~ ${data.period.endDate})` },
        { '항목': '집계 기준', '내용': data.basis },
        ...data.limitations.map((l, i) => ({ '항목': `한계 ${i + 1}`, '내용': l })),
        { '항목': '분류 정책', '내용': '연장근무 = 일반 근무일(평일·토요일·놀금)의 승인 근무시간 전체(야간 포함) / 야간근무 = 그중 22:00~06:00 / 휴일근무 = 휴일(일요일·공휴일·대체공휴일)의 승인 근무시간 전체(휴일 야간 포함) / 휴일 야간근무 = 그중 22:00~06:00. 자정을 넘는 근무는 날짜별로 나눠 적용' },
        { '항목': '표시 기준', '내용': `${INCLUSIVE_WORK_NOTE} ${INCLUSIVE_WORK_TOTAL_NOTE}` },
        { '항목': '근무 합계', '내용': `연장 ${formatMinutesHm(sd.overtime)}(야간 ${formatMinutesHm(sd.night)} 포함) + 휴일 ${formatMinutesHm(sd.holiday)}(휴일 야간 ${formatMinutesHm(sd.holidayNight)} 포함) = ${formatMinutesHm(data.summary.totalMinutes)}` },
        { '항목': '참고', '내용': '관리용 시간 구분이며 법정 근로시간·가산수당 판정과는 별개입니다. 원본 승인 정보는 변경하지 않습니다.' },
        { '항목': '집계 예외', '내용': `${data.summary.exceptionCount}건 (해당 월 ${formatMinutesHm(data.summary.exceptionMinutes)}) — 합계에서 제외, 근무근거 시트 참고` },
      ], '집계기준');
      XLSX.writeFile(wb, `연장근무관리_KR_${data.month}_${formatLocalYmd(new Date())}.xlsx`);
    } catch (err: any) {
      console.error('[HrOvertimeManagementPage] 엑셀 다운로드 실패:', err);
      alert(err?.message || '엑셀 다운로드 중 오류가 발생했습니다.');
    } finally {
      setIsExporting(false);
    }
  };

  const exportDisabled = isExporting || loading || !!error || !data;
  const detailEmployee = detailId ? data?.employees.find((e) => e.employeeId === detailId) ?? null : null;
  const s = data?.summary;
  const sd = s ? toInclusiveWorkDisplay(s) : null;

  return (
    <div style={{ padding: '24px', display: 'flex', flexDirection: 'column', height: 'calc(100vh - 120px)', minHeight: 0, overflow: 'hidden' }}>
      {/* 헤더 */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px', flexShrink: 0, gap: '12px', flexWrap: 'wrap' }}>
        <h1 style={{ fontSize: '24px', fontWeight: 'bold' }}>연장 근무 관리</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={handleExcelDownload}
            disabled={exportDisabled}
            title={isExporting ? '다운로드 중...' : '연장 근무 관리 엑셀 다운로드'}
            style={{
              padding: '8px 16px', background: exportDisabled ? 'var(--color-gray-400)' : 'var(--color-green-600)', color: 'white',
              border: 'none', borderRadius: '6px', cursor: exportDisabled ? 'not-allowed' : 'pointer', fontSize: '14px',
              display: 'flex', alignItems: 'center', gap: '8px',
            }}
          >
            <Download size={16} />
            {isExporting ? '다운로드 중...' : '엑셀 다운로드'}
          </button>
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', background: 'var(--color-gray-100)', padding: '8px 16px', borderRadius: '8px' }}>
            <button onClick={() => setMonth((m) => addMonths(m, -1))} title="이전 월"
              style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', color: 'var(--color-gray-600)' }}>
              <ChevronLeft size={20} />
            </button>
            <span data-testid="otm-month" style={{ fontSize: '16px', fontWeight: 600, minWidth: '120px', textAlign: 'center' }}>{formatMonthDisplay(month)}</span>
            {canGoNext ? (
              <button onClick={() => setMonth((m) => addMonths(m, 1))} title="다음 월"
                style={{ padding: '4px', background: 'transparent', border: 'none', cursor: 'pointer', display: 'flex', color: 'var(--color-gray-600)' }}>
                <ChevronRight size={20} />
              </button>
            ) : <div style={{ width: '28px' }} />}
          </div>
        </div>
      </div>

      {/* 집계 기준 안내 */}
      <div data-testid="otm-basis" style={{
        marginBottom: '16px', padding: '10px 14px', background: 'var(--color-blue-50, #eff6ff)', border: '1px solid var(--color-blue-200, #bfdbfe)',
        borderRadius: '8px', fontSize: '13px', lineHeight: 1.6, color: 'var(--color-gray-700)', flexShrink: 0,
      }}>
        <strong>승인 근무시간 기준</strong> — KR 전사원의 승인된 근무 신청 시간을 회사 정책에 따라 재분류했습니다.
        <ul style={{ margin: '4px 0 0', paddingLeft: '18px' }}>
          <li>일반 근무일(평일·토요일·놀금): 승인 근무시간 전체 → <strong>연장근무</strong>, 그중 22:00~06:00 → <strong>야간근무</strong></li>
          <li>휴일(일요일·공휴일·대체공휴일): 승인 근무시간 전체 → <strong>휴일근무</strong>, 그중 22:00~06:00 → <strong>휴일 야간근무</strong></li>
          <li data-testid="otm-inclusive-note"><strong>{INCLUSIVE_WORK_NOTE}</strong> {INCLUSIVE_WORK_TOTAL_NOTE}</li>
          <li>자정을 넘는 근무는 날짜별로 나눠 적용합니다.</li>
          <li>실제 출퇴근 시각이 아니며, 승인 기록에 휴게시간 정보가 없어 휴게시간을 차감하지 않았습니다.</li>
          <li>관리용 시간 구분이며 법정 근로시간·가산수당 판정과는 별개입니다.</li>
          <li>대체휴무 지급 = 실제 지급일 기준 유효 지급일수 × 8시간 (연장근무와 자동 연결되지 않음). 표시 단위: 시간:분</li>
        </ul>
      </div>

      {/* 검색 + 요약 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '16px', flexWrap: 'wrap', marginBottom: '16px', flexShrink: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--color-gray-50)', borderRadius: '6px', padding: '6px 12px', border: '1px solid var(--color-gray-200)' }}>
          <Search size={16} style={{ color: 'var(--color-gray-400)' }} />
          <input type="text" placeholder="직원 이름 검색" value={searchText} onChange={(e) => setSearchText(e.target.value)}
            style={{ border: 'none', background: 'transparent', outline: 'none', fontSize: '13px', width: '180px' }} />
        </div>
        {!loading && !error && s && sd && (
          <div data-testid="otm-summary" style={{ fontSize: '14px', color: 'var(--color-gray-600)', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <span>{keyword ? `검색 결과 ${filteredEmployees.length}명 / 전체 ${sortedEmployees.length}명` : `총 ${sortedEmployees.length}명`} (KR 전사)</span>
            <span>연장 {formatMinutesHm(sd.overtime)}</span>
            <span>야간 {formatMinutesHm(sd.night)}</span>
            <span>휴일 {formatMinutesHm(sd.holiday)}</span>
            <span>휴일 야간 {formatMinutesHm(sd.holidayNight)}</span>
            <span data-testid="otm-total" title="연장근무 + 휴일근무">합계(연장+휴일) {formatMinutesHm(s.totalMinutes)}</span>
            <span>대체휴무 지급 {formatMinutesHm(s.compOffGrantMinutes)}</span>
            {s.exceptionCount > 0 && (
              <span data-testid="otm-exceptions" style={{ color: 'var(--color-orange-600, #ea580c)', display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                <AlertTriangle size={14} /> 집계 예외 {s.exceptionCount}건 ({formatMinutesHm(s.exceptionMinutes)}, 합계 제외)
              </span>
            )}
          </div>
        )}
      </div>

      {loading && (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '60px', color: 'var(--color-gray-500)', flexShrink: 0 }}>
          <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', marginRight: '8px' }} /> 불러오는 중...
        </div>
      )}
      {!loading && error && (
        <div data-testid="otm-error" style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '16px', background: 'var(--color-red-50, #fef2f2)', color: 'var(--color-red-600, #dc2626)', borderRadius: '8px', flexShrink: 0 }}>
          <AlertCircle size={20} /> {error}
        </div>
      )}

      {!loading && !error && data && (
        <div style={{ flex: 1, minHeight: 0, overflow: 'auto', border: '1px solid var(--color-gray-200)', borderRadius: '8px' }}>
          <table data-testid="otm-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={th}>이름</th>
                <th style={th}>소속 부서</th>
                <th style={th}>직위</th>
                <th style={th}>직책</th>
                <th style={thNum}>연장근무(시간)</th>
                <th style={thNum}>야간근무(시간)</th>
                <th style={thNum}>휴일근무(시간)</th>
                <th style={thNum}>휴일 야간근무(시간)</th>
                <th style={thNum}>대체휴무 지급(시간)</th>
              </tr>
            </thead>
            <tbody>
              {filteredEmployees.length === 0 ? (
                <tr><td colSpan={9} style={{ ...td, textAlign: 'center', color: 'var(--color-gray-500)', padding: '40px' }}>조회된 직원이 없습니다.</td></tr>
              ) : filteredEmployees.map((e) => {
                const d = toInclusiveWorkDisplay(e);
                return (
                <tr key={e.employeeId} data-testid="otm-row">
                  <td style={td}>
                    <button style={linkBtn} onClick={() => setDetailId(e.employeeId)}>{e.name || '-'}</button>
                    {e.exceptionCount > 0 && <span title="집계 예외 있음" style={{ marginLeft: '6px', color: 'var(--color-orange-600, #ea580c)' }}>⚠</span>}
                    {e.outOfScope && <span title="대상 기준(입사·퇴사) 외 — 해당 월 기록 있음" style={{ marginLeft: '6px', fontSize: '11px', color: 'var(--color-gray-500)' }}>(대상외)</span>}
                  </td>
                  <td style={td}>{e.orgUnitName || '-'}</td>
                  <td style={td}>{e.positionName || '-'}</td>
                  <td style={td}>{e.jobTitleName || '-'}</td>
                  {[d.overtime, d.night, d.holiday, d.holidayNight, e.compOffGrantMinutes].map((m, i) => (
                    <td key={i} style={tdNum}>
                      <button style={{ ...linkBtn, color: m ? 'inherit' : 'var(--color-gray-400)' }} onClick={() => setDetailId(e.employeeId)}>{formatMinutesHm(m)}</button>
                    </td>
                  ))}
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {detailEmployee && data && (
        <OvertimeDetailModal
          employee={detailEmployee}
          month={data.month}
          requests={data.requests.filter((r) => r.employeeId === detailEmployee.employeeId)}
          grants={data.compOffGrants.filter((g) => g.employeeId === detailEmployee.employeeId)}
          onClose={() => setDetailId(null)}
        />
      )}
    </div>
  );
}

function OvertimeDetailModal({ employee, month, requests, grants, onClose }: {
  employee: OvertimeEmployeeRow;
  month: string;
  requests: OvertimeRequestRow[];
  grants: CompOffGrantDetailRow[];
  onClose: () => void;
}) {
  const ed = toInclusiveWorkDisplay(employee);
  return (
    <div data-testid="otm-detail" role="dialog" aria-label={`${employee.name} 연장 근무 근거`}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}
      onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()}
        style={{ background: 'white', borderRadius: '12px', width: 'min(1200px, 100%)', maxHeight: '90vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 20px', borderBottom: '1px solid var(--color-gray-200)' }}>
          <div>
            <div style={{ fontSize: '18px', fontWeight: 700 }}>{employee.name} · {formatMonthDisplay(month)} 집계 근거</div>
            <div style={{ fontSize: '13px', color: 'var(--color-gray-600)', marginTop: '4px' }}>
              {employee.orgUnitName || '-'} · 연장 {formatMinutesHm(ed.overtime)} · 야간 {formatMinutesHm(ed.night)} · 휴일 {formatMinutesHm(ed.holiday)} · 휴일 야간 {formatMinutesHm(ed.holidayNight)} · 합계(연장+휴일) {formatMinutesHm(employee.totalMinutes)} · 대체휴무 지급 {formatMinutesHm(employee.compOffGrantMinutes)} (승인 근무시간 기준)
            </div>
            <div data-testid="otm-detail-note" style={{ fontSize: '12px', color: 'var(--color-gray-500)', marginTop: '2px' }}>{INCLUSIVE_WORK_NOTE}</div>
          </div>
          <button onClick={onClose} aria-label="닫기" style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-gray-500)' }}><X size={20} /></button>
        </div>
        <div style={{ overflow: 'auto', padding: '16px 20px' }}>
          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 8px' }}>승인 근무 신청 ({requests.length}건)</h3>
          <div style={{ overflowX: 'auto', marginBottom: '20px' }}>
            <table data-testid="otm-detail-requests" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr>
                  {/* V70-S-4: '승인 신청 ID' 열은 화면·엑셀 근무근거 시트에서 제거 (행 key 로만 사용) */}
                  <th style={th}>근무일자</th><th style={th}>원래 신청 유형</th><th style={th}>승인 시각</th>
                  <th style={thNum}>최종 승인시간</th><th style={thNum}>연장</th><th style={thNum}>야간</th><th style={thNum}>휴일</th><th style={thNum}>휴일 야간</th>
                  <th style={th}>재분류 구간</th><th style={th}>상태</th><th style={th}>승인 후 수정</th><th style={th}>집계 예외</th>
                </tr>
              </thead>
              <tbody>
                {requests.length === 0 ? (
                  <tr><td colSpan={12} style={{ ...td, textAlign: 'center', color: 'var(--color-gray-500)' }}>해당 월 승인 근무 신청이 없습니다.</td></tr>
                ) : requests.map((r) => {
                  const rd = toInclusiveWorkDisplay(r);
                  return (
                  <tr key={r.id} data-testid="otm-detail-request" style={r.exception ? { background: 'var(--color-orange-50, #fff7ed)' } : undefined}>
                    <td style={td}>{r.date}</td>
                    <td style={td}>{ORIGINAL_TYPE_LABELS[r.originalType] || r.originalType || '-'}</td>
                    <td style={td}>{approvedRangeText(r)}</td>
                    <td style={tdNum}>{formatMinutesHm(r.approvedMinutes)}</td>
                    <td style={tdNum}>{formatMinutesHm(rd.overtime)}</td>
                    <td style={tdNum}>{formatMinutesHm(rd.night)}</td>
                    <td style={tdNum}>{formatMinutesHm(rd.holiday)}</td>
                    <td style={tdNum}>{formatMinutesHm(rd.holidayNight)}</td>
                    <td style={{ ...td, whiteSpace: 'normal', minWidth: '200px' }}>{segmentText(r)}</td>
                    <td style={td}>{r.status === 'approved' ? '승인' : r.status}</td>
                    <td style={td}>{r.modified ? '예' : '아니오'}</td>
                    <td style={{ ...td, whiteSpace: 'normal', minWidth: '160px', color: r.exception ? 'var(--color-orange-700, #c2410c)' : undefined }}>
                      {r.exception ? `${EXCEPTION_LABELS[r.exception.code] || r.exception.code} — ${r.exception.message}` : '-'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <h3 style={{ fontSize: '15px', fontWeight: 600, margin: '0 0 8px' }}>대체휴무 지급 ({grants.length}건, 지급일 기준)</h3>
          <div style={{ overflowX: 'auto' }}>
            <table data-testid="otm-detail-grants" style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr><th style={th}>지급일</th><th style={thNum}>지급량(일)</th><th style={thNum}>환산시간</th><th style={th}>지급 사유</th><th style={th}>취소 여부</th></tr>
              </thead>
              <tbody>
                {grants.length === 0 ? (
                  <tr><td colSpan={5} style={{ ...td, textAlign: 'center', color: 'var(--color-gray-500)' }}>해당 월 대체휴무 지급이 없습니다.</td></tr>
                ) : grants.map((g) => (
                  <tr key={g.id} data-testid="otm-detail-grant" style={g.cancelled ? { color: 'var(--color-gray-400)' } : undefined}>
                    <td style={td}>{g.grantedDate}</td>
                    <td style={tdNum}>{formatDays(g.days)}</td>
                    <td style={tdNum}>{formatMinutesHm(g.minutes)}</td>
                    <td style={{ ...td, whiteSpace: 'normal' }}>{g.reason || '-'}</td>
                    <td style={td}>{g.cancelled ? '취소 (합계 제외)' : g.counted ? '유효' : g.status || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
